import React, { useEffect, useState } from 'react';
import api from '../services/api';

const GaugePanel = ({ device, user, onClose, viewPreferences = {}, commandPreferences = {} }) => {
  const [currentDevice, setCurrentDevice] = useState(device);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [panelSize, setPanelSize] = useState('normal'); // 'small', 'normal', 'large', 'xlarge'


  // Auto-refresh cada 30 segundos
  useEffect(() => {
    const refreshDeviceData = async () => {
      try {
        console.log('🔄 Refreshing gauge data for:', device.name);
        const response = await api.get(`/api/devices/${device._id}`);
        setCurrentDevice(response.data);
        setLastUpdate(new Date());
      } catch (error) {
        console.error('Error refreshing device data:', error);
      }
    };

    // Refresh inicial
    refreshDeviceData();

    // Configurar interval para refresh cada 30 segundos
    const interval = setInterval(refreshDeviceData, 30000);

    // Cleanup al desmontar el componente
    return () => clearInterval(interval);
  }, [device._id, device.name]);

  if (!device) return null;

  // Obtener magnitudes del dispositivo (numéricas y booleanas) filtradas por permisos
  const getMagnitudes = () => {
    if (!currentDevice.magnitudes || currentDevice.magnitudes.length === 0) {
      return [];
    }

    // Filtrar magnitudes básico (incluir showInDashboard)
    let availableMagnitudes = currentDevice.magnitudes.filter(mag =>
      mag.showInDashboard !== false &&
      mag.currentValue !== null &&
      mag.currentValue !== undefined &&
      (mag.valueType === 'numeric' || mag.valueType === 'boolean')
    );

    // Para usuarios NO admin, filtrar según devicePermissions
    if (user && user.role !== 'admin') {
      if (user.devicePermissions) {
        const deviceId = currentDevice._id;
        const devicePermissions = user.devicePermissions[deviceId];

        if (devicePermissions && devicePermissions.magnitudes) {
          availableMagnitudes = availableMagnitudes.filter(magnitude => {
            return devicePermissions.magnitudes.includes(magnitude.name);
          });
        } else {
          availableMagnitudes = [];
        }
      } else {
        availableMagnitudes = [];
      }
    }

    // FILTRAR POR PREFERENCIAS DE VISUALIZACIÓN DEL USUARIO (aplica a todos, incluido admin)
    const userViewPrefs = viewPreferences[currentDevice._id];
    if (userViewPrefs !== undefined && Array.isArray(userViewPrefs)) {
      if (userViewPrefs.length === 0) {
        // Si el array está vacío, no mostrar ninguna magnitud
        return [];
      } else {
        // Filtrar solo las magnitudes que el usuario quiere ver
        availableMagnitudes = availableMagnitudes.filter(magnitude =>
          userViewPrefs.includes(magnitude.name)
        );
      }
    }

    return availableMagnitudes;
  };

  const magnitudes = getMagnitudes();

  // Funciones para manejar comandos (movidas desde Dashboard)
  const getEnabledCommands = (device) => {
    console.log('🔍 GaugePanel getEnabledCommands DEBUG:', {
      hasDevice: !!device,
      hasUser: !!user,
      userRole: user?.role,
      deviceName: device?.name,
      deviceId: device?._id,
      deviceCommands: device?.commands?.length || 0,
      devicePermissions: user?.devicePermissions?.[device?._id]
    });

    if (!device || !user) {
      console.log('❌ GaugePanel: No device or user');
      return [];
    }

    // Filtrar primero por showInDashboard
    let enabledCommands = (device.commands || []).filter(cmd => cmd.showInDashboard !== false);

    // Para usuarios NO admin, filtrar según devicePermissions
    if (user.role !== 'admin') {
      if (!user.devicePermissions || enabledCommands.length === 0) {
        console.log('❌ GaugePanel: Missing devicePermissions or no visible commands');
        return [];
      }

      const deviceId = device._id;
      const devicePermissions = user.devicePermissions[deviceId];

      if (!devicePermissions || !devicePermissions.commands) {
        console.log('❌ GaugePanel: No device permissions or commands for this device');
        return [];
      }

      enabledCommands = enabledCommands.filter(command => {
        const isEnabled = devicePermissions.commands.includes(command.name);
        console.log(`🎮 Command check: ${command.label} (${command.name}) - Enabled: ${isEnabled}`);
        return isEnabled;
      });
    }

    // FILTRAR POR PREFERENCIAS DE COMANDOS DEL USUARIO (aplica a todos, incluido admin)
    const userCommandPrefs = commandPreferences[device._id];
    if (userCommandPrefs !== undefined && Array.isArray(userCommandPrefs)) {
      if (userCommandPrefs.length === 0) {
        // Si el array está vacío, no mostrar ningún comando
        console.log('👁️ GaugePanel: User has no commands selected for device');
        return [];
      } else {
        // Filtrar solo los comandos que el usuario quiere ver
        enabledCommands = enabledCommands.filter(command =>
          userCommandPrefs.includes(command.name)
        );
        console.log(`👁️ GaugePanel: Filtered to ${enabledCommands.length} commands by user preferences`);
      }
    }

    console.log(`✅ GaugePanel: Returning ${enabledCommands.length} enabled commands`);
    return enabledCommands;
  };

  const getCommandButtonColor = (buttonColor) => {
    const colors = {
      primary: '#2563eb',
      success: '#10b981', 
      warning: '#f59e0b',
      danger: '#ef4444',
      info: '#06b6d4'
    };
    return colors[buttonColor] || colors.primary;
  };

  const sendCommand = async (device, command) => {
    try {
      // Mostrar confirmación personalizada
      const confirmMessage = `¿Realmente quiere realizar el comando "${command.label}" en el dispositivo "${device.name}"?`;
      const confirmed = window.confirm(confirmMessage);
      if (!confirmed) {
        return;
      }

      console.log(`🎮 Sending command to ${device.name}:`, command.label);
      
      const response = await api.sendCommand(device._id, command._id);
      
      if (response.data.message) {
        alert(`✅ Comando enviado: ${command.label}`);
        console.log('✅ Command sent successfully:', response.data);
        
        // Refrescar los datos del dispositivo después del comando
        setTimeout(async () => {
          try {
            const deviceResponse = await api.get(`/api/devices/${device._id}`);
            setCurrentDevice(deviceResponse.data);
            setLastUpdate(new Date());
          } catch (error) {
            console.error('Error refreshing device data after command:', error);
          }
        }, 1500);
      }
    } catch (error) {
      console.error('❌ Error sending command:', error);
      alert(`❌ Error al enviar comando: ${error.response?.data?.error || error.message}`);
    }
  };

  // Configuraciones de tamaño optimizadas para muchas magnitudes
  const getSizeConfig = () => {
    const numMagnitudes = magnitudes.length;
    
    const configs = {
      small: {
        width: numMagnitudes > 8 ? '85vw' : '70vw',
        maxWidth: numMagnitudes > 8 ? '1400px' : '1000px',
        gaugeSize: numMagnitudes > 12 ? 120 : numMagnitudes > 8 ? 130 : 140,
        gaugeHeight: numMagnitudes > 12 ? 80 : numMagnitudes > 8 ? 90 : 100,
        minGaugeWidth: numMagnitudes > 12 ? 140 : numMagnitudes > 8 ? 160 : 180,
        gridColumns: numMagnitudes > 12 ? 'repeat(auto-fit, minmax(140px, 1fr))' : 
                     numMagnitudes > 8 ? 'repeat(auto-fit, minmax(160px, 1fr))' : 
                     'repeat(auto-fit, minmax(180px, 1fr))'
      },
      normal: {
        width: numMagnitudes > 8 ? '90vw' : '80vw',
        maxWidth: numMagnitudes > 8 ? '1800px' : '1400px',
        gaugeSize: numMagnitudes > 12 ? 140 : numMagnitudes > 8 ? 155 : 170,
        gaugeHeight: numMagnitudes > 12 ? 100 : numMagnitudes > 8 ? 110 : 120,
        minGaugeWidth: numMagnitudes > 12 ? 160 : numMagnitudes > 8 ? 180 : 200,
        gridColumns: numMagnitudes > 12 ? 'repeat(auto-fit, minmax(160px, 1fr))' : 
                     numMagnitudes > 8 ? 'repeat(auto-fit, minmax(180px, 1fr))' : 
                     'repeat(auto-fit, minmax(200px, 1fr))'
      },
      large: {
        width: numMagnitudes > 8 ? '95vw' : '90vw',
        maxWidth: numMagnitudes > 8 ? '2200px' : '1800px',
        gaugeSize: numMagnitudes > 12 ? 160 : numMagnitudes > 8 ? 175 : 190,
        gaugeHeight: numMagnitudes > 12 ? 120 : numMagnitudes > 8 ? 130 : 140,
        minGaugeWidth: numMagnitudes > 12 ? 180 : numMagnitudes > 8 ? 200 : 220,
        gridColumns: numMagnitudes > 12 ? 'repeat(auto-fit, minmax(180px, 1fr))' : 
                     numMagnitudes > 8 ? 'repeat(auto-fit, minmax(200px, 1fr))' : 
                     'repeat(auto-fit, minmax(220px, 1fr))'
      },
      xlarge: {
        width: '98vw',
        maxWidth: numMagnitudes > 8 ? '2600px' : '2200px',
        gaugeSize: numMagnitudes > 12 ? 180 : numMagnitudes > 8 ? 195 : 210,
        gaugeHeight: numMagnitudes > 12 ? 140 : numMagnitudes > 8 ? 150 : 160,
        minGaugeWidth: numMagnitudes > 12 ? 200 : numMagnitudes > 8 ? 220 : 240,
        gridColumns: numMagnitudes > 12 ? 'repeat(auto-fit, minmax(200px, 1fr))' : 
                     numMagnitudes > 8 ? 'repeat(auto-fit, minmax(220px, 1fr))' : 
                     'repeat(auto-fit, minmax(240px, 1fr))'
      }
    };
    return configs[panelSize];
  };

  const sizeConfig = getSizeConfig();

  // Calcular rango automático para cada magnitud
  const calculateRange = (magnitude) => {
    const value = parseFloat(magnitude.currentValue);
    let min, max, center;

    // Si hay valores normales definidos, usarlos como referencia
    if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
      // El centro del gauge será el promedio del rango normal
      center = (magnitude.minNormalValue + magnitude.maxNormalValue) / 2;
      const normalRange = magnitude.maxNormalValue - magnitude.minNormalValue;
      const buffer = normalRange * 0.3; // 30% de buffer
      
      // Crear rango simétrico alrededor del centro
      const halfRange = (normalRange / 2) + buffer;
      min = center - halfRange;
      max = center + halfRange;
    } else {
      // Calcular rango automático basado en el valor actual
      const absValue = Math.abs(value);
      if (absValue === 0) {
        min = -10;
        max = 10;
        center = 0;
      } else {
        const range = absValue * 1.5; // 150% del valor como rango
        center = value;
        min = center - range;
        max = center + range;
      }
    }

    // Asegurar que el valor actual esté dentro del rango
    if (value < min) {
      const adjustment = min - value + Math.abs(value * 0.1);
      min -= adjustment;
      max -= adjustment;
    }
    if (value > max) {
      const adjustment = value - max + Math.abs(value * 0.1);
      min += adjustment;
      max += adjustment;
    }

    return { min, max, center };
  };

  // Componente individual de gauge/velocímetro
  const Gauge = ({ magnitude }) => {
    const value = parseFloat(magnitude.currentValue);
    const { min, max, center } = calculateRange(magnitude);
    const range = max - min;
    
    // Calcular ángulo de la aguja
    // Si hay valores normales, el centro (promedio normal) estará arriba (0°)
    // Sino, mantener comportamiento original (-90° a +90°)
    let angle;
    if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
      // Rango de -135° a +45° (total 180°) con el centro en -45° (arriba)
      const percentage = Math.min(Math.max((value - min) / range, 0), 1);
      angle = -135 + (percentage * 180);
    } else {
      // Comportamiento original: de -90° a +90°
      const percentage = Math.min(Math.max((value - min) / range, 0), 1);
      angle = -90 + (percentage * 180);
    }
    
    // Determinar color según rango normal
    let needleColor = '#3b82f6'; // Azul por defecto
    if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
      if (value < magnitude.minNormalValue || value > magnitude.maxNormalValue) {
        needleColor = '#ef4444'; // Rojo fuera de rango
      } else {
        needleColor = '#10b981'; // Verde en rango normal
      }
    }

    // Generar marcas en el gauge
    const generateTicks = () => {
      const ticks = [];
      const tickCount = 5; // Número de marcas principales
      
      for (let i = 0; i <= tickCount; i++) {
        const tickValue = min + (range * i / tickCount);
        
        // Calcular ángulo según el tipo de gauge
        let tickAngle;
        if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
          // Gauge con rango normal: de -135° a +45°
          tickAngle = -135 + (i * 180 / tickCount);
        } else {
          // Gauge estándar: de -90° a +90°
          tickAngle = -90 + (i * 180 / tickCount);
        }
        
        const isInNormalRange = magnitude.minNormalValue !== null && 
                               magnitude.maxNormalValue !== null &&
                               tickValue >= magnitude.minNormalValue && 
                               tickValue <= magnitude.maxNormalValue;
        
        ticks.push({
          angle: tickAngle,
          value: tickValue,
          isNormal: isInNormalRange
        });
      }
      return ticks;
    };

    const ticks = generateTicks();
    
    // Constantes para el gauge corregidas
    // Constantes escaladas según el tamaño
    const centerX = sizeConfig.gaugeSize / 2;
    const centerY = sizeConfig.gaugeHeight / 2;
    const radius = Math.min(sizeConfig.gaugeSize, sizeConfig.gaugeHeight) * 0.35;
    
    return (
      <div style={{
        background: 'linear-gradient(145deg, #1a1a2e, #16213e)',
        borderRadius: '12px',
        padding: magnitudes.length > 12 ? '0.75rem' : magnitudes.length > 8 ? '1rem' : '1.25rem',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        minHeight: magnitudes.length > 12 ? '180px' : magnitudes.length > 8 ? '200px' : '240px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Efecto de brillo de fondo */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
          animation: 'pulse 4s ease-in-out infinite',
          zIndex: 0
        }}></div>

        {/* Header con icono y título */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: magnitudes.length > 12 ? '0.25rem' : magnitudes.length > 8 ? '0.5rem' : '0.75rem',
          marginBottom: magnitudes.length > 12 ? '0.5rem' : magnitudes.length > 8 ? '0.75rem' : '1rem',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            borderRadius: magnitudes.length > 8 ? '8px' : '12px',
            padding: magnitudes.length > 12 ? '0.25rem' : magnitudes.length > 8 ? '0.375rem' : '0.5rem',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ 
              fontSize: magnitudes.length > 8 ? '1.2rem' : '1.5rem',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
            }}>
              {magnitude.icon || '📊'}
            </span>
          </div>
          <h3 style={{
            margin: 0,
            fontSize: magnitudes.length > 12 ? '0.95rem' : magnitudes.length > 8 ? '1.05rem' : '1.2rem',
            fontWeight: '700',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
            letterSpacing: '0.025em'
          }}>
            {magnitude.label}
          </h3>
        </div>

        {/* Gauge SVG - Corregido */}
        <div style={{ 
          position: 'relative', 
          width: `${sizeConfig.gaugeSize}px`, 
          height: `${sizeConfig.gaugeHeight}px`, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 60%)',
          borderRadius: '50%',
          boxShadow: 'inset 0 4px 8px rgba(0, 0, 0, 0.3)',
          zIndex: 1
        }}>
          <svg 
            width={sizeConfig.gaugeSize} 
            height={sizeConfig.gaugeHeight} 
            viewBox={`0 0 ${sizeConfig.gaugeSize} ${sizeConfig.gaugeHeight}`}
          >
            {/* Arco principal del gauge */}
            {(() => {
              // Calcular el arco según el tipo de gauge
              if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
                // Arco de -135° a +45° (con centro arriba)
                const startX = centerX + radius * Math.cos(-135 * Math.PI / 180);
                const startY = centerY + radius * Math.sin(-135 * Math.PI / 180);
                const endX = centerX + radius * Math.cos(45 * Math.PI / 180);
                const endY = centerY + radius * Math.sin(45 * Math.PI / 180);
                
                return (
                  <path
                    d={`M ${startX} ${startY} A ${radius} ${radius} 0 1 1 ${endX} ${endY}`}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.2)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    filter="drop-shadow(0 0 4px rgba(255, 255, 255, 0.1))"
                  />
                );
              } else {
                // Arco estándar de -90° a +90°
                return (
                  <path
                    d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.2)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    filter="drop-shadow(0 0 4px rgba(255, 255, 255, 0.1))"
                  />
                );
              }
            })()}
            
            {/* Rangos de colores usando múltiples segmentos pequeños */}
            {magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null && (
              <>
                {/* Generar segmentos uniformes para el gauge */}
                {Array.from({length: 60}, (_, i) => {
                  // Adaptar ángulos según tipo de gauge
                  let segmentAngle, nextSegmentAngle;
                  if (magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null) {
                    // Gauge con centro arriba: -135° a +45°
                    segmentAngle = -135 + (i * 3); // Cada segmento ocupa 3 grados
                    nextSegmentAngle = segmentAngle + 3;
                  } else {
                    // Gauge estándar: -90° a +90°
                    segmentAngle = -90 + (i * 3);
                    nextSegmentAngle = segmentAngle + 3;
                  }
                  
                  const segmentPercent = i / 60;
                  const segmentValue = min + (range * segmentPercent);
                  
                  // Determinar color del segmento con efectos brillantes
                  let segmentColor = 'rgba(255, 255, 255, 0.1)'; // Gris transparente por defecto
                  if (segmentValue >= magnitude.minNormalValue && segmentValue <= magnitude.maxNormalValue) {
                    segmentColor = '#00ff88'; // Verde neón para rango normal
                  } else {
                    segmentColor = '#ff3366'; // Rojo neón para fuera del rango
                  }
                  
                  const startX = centerX + radius * Math.cos(segmentAngle * Math.PI / 180);
                  const startY = centerY + radius * Math.sin(segmentAngle * Math.PI / 180);
                  const endX = centerX + radius * Math.cos(nextSegmentAngle * Math.PI / 180);
                  const endY = centerY + radius * Math.sin(nextSegmentAngle * Math.PI / 180);
                  
                  return (
                    <path
                      key={i}
                      d={`M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`}
                      fill="none"
                      stroke={segmentColor}
                      strokeWidth="10"
                      opacity="0.9"
                      filter="drop-shadow(0 0 6px currentColor)"
                      style={{ filter: `drop-shadow(0 0 6px ${segmentColor})` }}
                    />
                  );
                })}
              </>
            )}
            
            {/* Marcas del gauge corregidas */}
            {ticks.map((tick, index) => {
              // Convertir ángulo a radianes para cálculos correctos
              const angleRad = (tick.angle * Math.PI) / 180;
              const x1 = centerX + (radius - 10) * Math.cos(angleRad);
              const y1 = centerY + (radius - 10) * Math.sin(angleRad);
              const x2 = centerX + radius * Math.cos(angleRad);
              const y2 = centerY + radius * Math.sin(angleRad);
              
              // Posición del texto
              const textX = centerX + (radius + 15) * Math.cos(angleRad);
              const textY = centerY + (radius + 15) * Math.sin(angleRad);
              
              return (
                <g key={index}>
                  <line
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={tick.isNormal ? '#00ff88' : 'rgba(255, 255, 255, 0.6)'}
                    strokeWidth="3"
                    filter="drop-shadow(0 0 3px currentColor)"
                  />
                  <text
                    x={textX}
                    y={textY + 3}
                    textAnchor="middle"
                    fontSize="11"
                    fill="rgba(255, 255, 255, 0.9)"
                    fontWeight="600"
                    style={{ textShadow: '0 0 6px rgba(255, 255, 255, 0.5)' }}
                  >
                    {tick.value.toFixed(magnitude.decimals || 0)}
                  </text>
                </g>
              );
            })}
            
            {/* Aguja corregida con efectos */}
            <g>
              {/* Sombra de la aguja */}
              <line
                x1={centerX + 2}
                y1={centerY + 2}
                x2={centerX + (radius - 20) * Math.cos(angle * Math.PI / 180) + 2}
                y2={centerY + (radius - 20) * Math.sin(angle * Math.PI / 180) + 2}
                stroke="rgba(0, 0, 0, 0.3)"
                strokeWidth="4"
                strokeLinecap="round"
              />
              {/* Aguja principal */}
              <line
                x1={centerX}
                y1={centerY}
                x2={centerX + (radius - 20) * Math.cos(angle * Math.PI / 180)}
                y2={centerY + (radius - 20) * Math.sin(angle * Math.PI / 180)}
                stroke={needleColor}
                strokeWidth="4"
                strokeLinecap="round"
                filter="drop-shadow(0 0 8px currentColor)"
                style={{ filter: `drop-shadow(0 0 8px ${needleColor})` }}
              />
              {/* Centro de la aguja con efectos */}
              <circle
                cx={centerX + 1} 
                cy={centerY + 1} 
                r="6"
                fill="rgba(0, 0, 0, 0.3)"
              />
              <circle
                cx={centerX} 
                cy={centerY} 
                r="6"
                fill={needleColor}
                filter="drop-shadow(0 0 6px currentColor)"
                style={{ filter: `drop-shadow(0 0 6px ${needleColor})` }}
              />
              <circle
                cx={centerX} 
                cy={centerY} 
                r="3"
                fill="rgba(255, 255, 255, 0.3)"
              />
            </g>
            
            {/* Valor en la parte inferior con efectos */}
            <text
              x={centerX} 
              y={sizeConfig.gaugeHeight - 10}
              textAnchor="middle"
              fontSize={Math.max(14, sizeConfig.gaugeSize * 0.08)}
              fontWeight="bold"
              fill={needleColor}
              style={{ 
                filter: `drop-shadow(0 0 8px ${needleColor})`,
                textShadow: `0 0 10px ${needleColor}`
              }}
            >
              {value.toFixed(magnitude.decimals || 1)}{magnitude.unit || ''}
            </text>
          </svg>
        </div>


      </div>
    );
  };

  // Componente para indicadores booleanos
  const BooleanIndicator = ({ magnitude }) => {
    const value = magnitude.currentValue;
    const isOn = value === true || value === 'true' || value === 'on' || value === 1 || value === '1';
    
    // Normalizar string values to boolean
    let normalizedValue = value;
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase();
      if (lowerValue === 'true' || lowerValue === '1' || lowerValue === 'on' || lowerValue === 'yes') {
        normalizedValue = true;
      } else if (lowerValue === 'false' || lowerValue === '0' || lowerValue === 'off' || lowerValue === 'no') {
        normalizedValue = false;
      }
    }

    const isNormalState = magnitude.normalState ? 
      (magnitude.normalState === 'true' ? normalizedValue === true : normalizedValue === false) : 
      true; // Si no hay normalState definido, asumir que cualquier estado es normal

    const indicatorColor = isNormalState ? 
      (magnitude.normalColor || '#10b981') : 
      (magnitude.abnormalColor || '#ef4444');

    return (
      <div style={{
        background: 'linear-gradient(145deg, #1a1a2e, #16213e)',
        borderRadius: '12px',
        padding: magnitudes.length > 12 ? '0.75rem' : magnitudes.length > 8 ? '1rem' : '1.25rem',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        minHeight: magnitudes.length > 12 ? '160px' : magnitudes.length > 8 ? '180px' : '220px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Efecto de brillo de fondo */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: `radial-gradient(circle, ${indicatorColor}20 0%, transparent 70%)`,
          animation: normalizedValue ? 'pulse 2s ease-in-out infinite' : 'none',
          zIndex: 0
        }}></div>

        {/* Header con icono y título */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: magnitudes.length > 12 ? '0.25rem' : magnitudes.length > 8 ? '0.5rem' : '0.75rem',
          marginBottom: magnitudes.length > 12 ? '0.5rem' : magnitudes.length > 8 ? '0.75rem' : '1rem',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${indicatorColor}, ${indicatorColor}CC)`,
            borderRadius: magnitudes.length > 8 ? '8px' : '12px',
            padding: magnitudes.length > 12 ? '0.25rem' : magnitudes.length > 8 ? '0.375rem' : '0.5rem',
            boxShadow: `0 4px 12px ${indicatorColor}50`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ 
              fontSize: magnitudes.length > 8 ? '1.2rem' : '1.5rem',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
            }}>
              {magnitude.icon || (normalizedValue ? '🟢' : '🔴')}
            </span>
          </div>
          <h3 style={{
            margin: 0,
            fontSize: magnitudes.length > 12 ? '0.95rem' : magnitudes.length > 8 ? '1.05rem' : '1.2rem',
            fontWeight: '700',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
            letterSpacing: '0.025em'
          }}>
            {magnitude.label}
          </h3>
        </div>

        {/* Indicador compacto */}
        <div style={{
          width: magnitudes.length > 12 ? '50px' : magnitudes.length > 8 ? '60px' : '70px',
          height: magnitudes.length > 12 ? '50px' : magnitudes.length > 8 ? '60px' : '70px',
          borderRadius: '50%',
          background: normalizedValue ? 
            `linear-gradient(135deg, ${indicatorColor}, ${indicatorColor}dd)` :
            'linear-gradient(135deg, #6b7280, #4b5563)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: normalizedValue ?
            `0 4px 12px ${indicatorColor}40` :
            '0 4px 12px rgba(0,0,0,0.2)',
          border: '2px solid rgba(255,255,255,0.2)',
          position: 'relative',
          transition: 'all 0.3s ease'
        }}>
          <div style={{
            fontSize: magnitudes.length > 12 ? '1.2rem' : magnitudes.length > 8 ? '1.4rem' : '1.6rem',
            color: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.125rem'
          }}>
            <span style={{ 
              fontSize: magnitudes.length > 12 ? '0.5rem' : magnitudes.length > 8 ? '0.55rem' : '0.6rem', 
              fontWeight: '600', 
              opacity: 0.9 
            }}>
              {normalizedValue ? 'ON' : 'OFF'}
            </span>
            <span>
              {normalizedValue ? '●' : '○'}
            </span>
          </div>
        </div>

        {/* Estado compacto */}
        <div style={{
          marginTop: magnitudes.length > 12 ? '0.125rem' : magnitudes.length > 8 ? '0.25rem' : '0.375rem',
          padding: '0.25rem 0.75rem',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: '600',
          color: normalizedValue ? indicatorColor : '#9ca3af',
          textAlign: 'center'
        }}>
          {normalizedValue ? 'ACTIVO' : 'INACTIVO'}
        </div>

      </div>
    );
  };

  if (magnitudes.length === 0) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <h2 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>
            Panel de Control - {currentDevice.name}
          </h2>
          <p style={{ color: '#6b7280', margin: '0 0 1.5rem 0' }}>
            No hay magnitudes disponibles para mostrar en el panel de control.
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      {/* Colores según estado del dispositivo */}
      {(() => {
        const isOnline = currentDevice.status === 'online';
        const borderColor = isOnline ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)';
        const glowColor = isOnline ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)';

        return (
      <div style={{
        background: 'linear-gradient(145deg, #0f172a, #1e293b)',
        borderRadius: '20px',
        padding: magnitudes.length > 12 ? '1.25rem' : magnitudes.length > 8 ? '1.5rem' : '2rem',
        width: sizeConfig.width,
        maxWidth: sizeConfig.maxWidth,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: `0 25px 50px rgba(0, 0, 0, 0.5), 0 0 20px ${isOnline ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
        border: `2px solid ${borderColor}`,
        position: 'relative'
      }}>
        {/* Efecto de brillo en el fondo del panel */}
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          height: '100px',
          background: `linear-gradient(180deg, ${glowColor} 0%, transparent 100%)`,
          borderRadius: '20px 20px 0 0',
          zIndex: 0
        }}></div>

        {/* Header del panel */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: magnitudes.length > 12 ? '1.25rem' : magnitudes.length > 8 ? '1.5rem' : '2rem',
          paddingBottom: magnitudes.length > 12 ? '1rem' : magnitudes.length > 8 ? '1.25rem' : '1.5rem',
          borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          zIndex: 1
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '1.8rem',
              fontWeight: '800',
              color: '#ffffff',
              textShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <span style={{
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                borderRadius: '12px',
                padding: '0.5rem',
                fontSize: '1.5rem',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
              }}>
                🎛️
              </span>
              Panel de Control
            </h2>
            <p style={{
              margin: '0.5rem 0 0 0',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '1rem',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap'
            }}>
              <span style={{ color: isOnline ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                {currentDevice.name}
              </span>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.2rem 0.5rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '600',
                background: isOnline ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                color: isOnline ? '#10b981' : '#ef4444',
                border: `1px solid ${isOnline ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`
              }}>
                <span style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: isOnline ? '#10b981' : '#ef4444',
                  boxShadow: `0 0 6px ${isOnline ? '#10b981' : '#ef4444'}`
                }}></span>
                {isOnline ? 'En línea' : 'Desconectado'}
              </span>
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>•</span>
              <span style={{ color: '#00ff88' }}>
                {magnitudes.length} indicador{magnitudes.length !== 1 ? 'es' : ''}
              </span>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {/* Control de tamaño */}
            <div style={{
              display: 'flex',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '0.5rem',
              gap: '0.5rem',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              {['small', 'normal', 'large', 'xlarge'].map((size) => (
                <button
                  key={size}
                  onClick={() => setPanelSize(size)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: panelSize === size 
                      ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
                      : 'transparent',
                    color: panelSize === size ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    transition: 'all 0.2s ease',
                    boxShadow: panelSize === size 
                      ? '0 2px 8px rgba(59, 130, 246, 0.4)' 
                      : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (panelSize !== size) {
                      e.target.style.background = 'rgba(59, 130, 246, 0.2)';
                      e.target.style.color = '#3b82f6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (panelSize !== size) {
                      e.target.style.background = 'transparent';
                      e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                    }
                  }}
                >
                  {size === 'small' ? 'S' : 
                   size === 'normal' ? 'M' : 
                   size === 'large' ? 'L' : 'XL'}
                </button>
              ))}
            </div>
            
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '1.3rem',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                transition: 'all 0.2s ease',
                position: 'relative',
                zIndex: 2
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1)';
                e.target.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Grid de gauges e indicadores */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: sizeConfig.gridColumns,
          gap: magnitudes.length > 12 ? '0.5rem' : magnitudes.length > 8 ? '0.75rem' : '1rem',
          justifyItems: 'center'
        }}>
          {magnitudes.map((magnitude, index) => (
            magnitude.valueType === 'boolean' ? (
              <BooleanIndicator key={index} magnitude={magnitude} />
            ) : (
              <Gauge key={index} magnitude={magnitude} />
            )
          ))}
        </div>

        {/* Comandos Simples */}
        {getEnabledCommands(currentDevice).length > 0 && (
          <div style={{
            marginTop: magnitudes.length > 12 ? '0.75rem' : magnitudes.length > 8 ? '1rem' : '1.25rem',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {getEnabledCommands(currentDevice).map((command, index) => (
              <button
                key={command._id || index}
                onClick={() => sendCommand(currentDevice, command)}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: getCommandButtonColor(command.buttonColor),
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.2s ease',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  minWidth: 'auto'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                }}
                title={command.description || `Ejecutar comando: ${command.label}`}
              >
                <span>{command.icon}</span>
                <span>{command.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Estilos CSS para animaciones */}
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>

      </div>
        );
      })()}
    </div>
  );
};

export default GaugePanel;